/**
 * Typed client for agency social scheduling (/v1/social/agency, behind the partner BFF; flag social.agency_scheduling).
 * Every call is scoped to one client workspace the agency holds an active grant for (else 404).
 *
 * Validation and best-time suggestions use the agency-scoped twins of the posting-guidance routes
 * (`.../posts/validate`, `.../best-times`, same grant check). They answer only while the client's `social.posting_guidance`
 * flag is on; otherwise `validate` and `bestTimes` resolve to null and the screen hides them.
 */
import type { AgencyAssetView, AgencyPostView, AgencySchedulePost, AgencySocialAccountView } from '@qm/contracts/social';
import { newIdempotencyKey, seg, type Envelope, type Http } from '../kit/http';

export type { AgencyAssetView, AgencyPostView, AgencySchedulePost, AgencySocialAccountView };

export interface ValidationIssue { code: string; message: string | null; severity: 'error' | 'warning' }
export interface ValidationResult { ok: boolean; issues: ValidationIssue[] }
export interface BestTime { at: string; label: string | null }

const client = (tenantId: string) => `/v1/social/agency/clients/${seg(tenantId)}` as const;

/** Tolerant reading of the validate answer: `{ ok, issues[] }` or `{ valid, errors[], warnings[] }`. */
export function readValidation(raw: unknown): ValidationResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const issue = (severity: 'error' | 'warning') => (x: unknown): ValidationIssue | null => {
    if (typeof x === 'string') return { code: x, message: null, severity };
    if (!x || typeof x !== 'object') return null;
    const o = x as Record<string, unknown>;
    const code = typeof o.code === 'string' ? o.code : typeof o.rule === 'string' ? o.rule : null;
    if (!code) return null;
    const sev = o.severity === 'warning' || o.level === 'warning' ? 'warning' : o.severity === 'blocking' ? 'error' : severity;
    return { code, message: typeof o.message === 'string' ? o.message : null, severity: sev };
  };
  const list = (v: unknown, s: 'error' | 'warning') => (Array.isArray(v) ? v.map(issue(s)).filter((x): x is ValidationIssue => !!x) : []);
  const issues = [...list(r.problems, 'error'), ...list(r.issues, 'error'), ...list(r.errors, 'error'), ...list(r.warnings, 'warning')];
  const ok = typeof r.ok === 'boolean' ? r.ok : typeof r.valid === 'boolean' ? r.valid : !issues.some((i) => i.severity === 'error');
  return { ok, issues };
}

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** The next instant (after `now`, on the hour) whose wall time in `timeZone` is that weekday + hour; null if none in 8 days. */
export function nextOccurrence(weekday: string, hour: number, timeZone: string, now: Date): string | null {
  let fmt: Intl.DateTimeFormat;
  try { fmt = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: 'numeric', hourCycle: 'h23' }); } catch { return null; }
  const start = Math.ceil(now.getTime() / 3_600_000) * 3_600_000;
  for (let i = 0; i < 8 * 24 + 2; i++) {
    const d = new Date(start + i * 3_600_000);
    const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
    if ((parts.weekday ?? '').toLowerCase().slice(0, 3) === weekday && Number(parts.hour) % 24 === hour) return d.toISOString();
  }
  return null;
}

/**
 * Best times as concrete upcoming instants. Reads `{ slots: [{ weekday, hour }], timeZone }` (the posting-guidance answer),
 * and also plain ISO strings or `{ at }` items. Soonest first, at most 5.
 */
export function readBestTimes(raw: unknown, now: Date = new Date()): BestTime[] | null {
  const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  const arr = Array.isArray(raw) ? raw : obj ? (obj.slots ?? obj.times) : null;
  if (!Array.isArray(arr)) return null;
  const tz = typeof obj?.timeZone === 'string' ? obj.timeZone : 'UTC';
  const out: BestTime[] = [];
  for (const x of arr) {
    const o = x && typeof x === 'object' ? (x as Record<string, unknown>) : null;
    let at: string | null = typeof x === 'string' ? x : typeof o?.at === 'string' ? o.at : typeof o?.start === 'string' ? o.start : null;
    if (!at && o && typeof o.weekday === 'string' && typeof o.hour === 'number' && (WEEKDAYS as readonly string[]).includes(o.weekday)) at = nextOccurrence(o.weekday, o.hour, tz, now);
    if (!at || Number.isNaN(Date.parse(at))) continue;
    out.push({ at, label: typeof o?.label === 'string' ? o.label : null });
  }
  return out.sort((a, b) => Date.parse(a.at) - Date.parse(b.at)).slice(0, 5);
}

export function socialApi(http: Http) {
  return {
    /** 404 feature_disabled while the flag is off: the menu lists the screen only when this answers. */
    availability: () => http.get<{ enabled: boolean }>('/v1/social/agency/availability'),
    accounts: (tenantId: string) => http.page<AgencySocialAccountView[]>(`${client(tenantId)}/accounts`) as Promise<Envelope<AgencySocialAccountView[]> & { meta?: { connectUrl?: string | null } }>,
    assets: (tenantId: string) => http.get<AgencyAssetView[]>(`${client(tenantId)}/assets`),
    posts: (tenantId: string) => http.get<AgencyPostView[]>(`${client(tenantId)}/posts`),
    schedule: (tenantId: string, body: AgencySchedulePost) => http.post<AgencyPostView>(`${client(tenantId)}/posts`, body, { idempotencyKey: newIdempotencyKey() }),
    cancel: (tenantId: string, postId: string) => http.post<{ id: string; status: 'cancelled' }>(`${client(tenantId)}/posts/${seg(postId)}/cancel`, {}),
    /** Post rules for a draft. Any failure (flag off 404, 403, network) -> null: hide it quietly. */
    validate: async (tenantId: string, body: { accountId: string; caption: string; assetIds: string[] }): Promise<ValidationResult | null> => {
      try { return readValidation(await http.post<unknown>(`${client(tenantId)}/posts/validate`, body)); } catch { return null; }
    },
    /** Best-time suggestions for one account. Any failure -> null: hide it quietly. */
    bestTimes: async (tenantId: string, accountId: string, now: Date = new Date()): Promise<BestTime[] | null> => {
      try { return readBestTimes(await http.get<unknown>(`${client(tenantId)}/best-times`, { accountId }), now); } catch { return null; }
    },
  };
}
export type SocialApi = ReturnType<typeof socialApi>;
